import * as i0 from '@angular/core';
import { PLATFORM_ID, Injectable, Inject, Directive, Input, NgModule, InjectionToken } from '@angular/core';
import { isPlatformBrowser, CommonModule } from '@angular/common';
import * as i1 from '@angular/router';
import { NavigationEnd, RouterModule } from '@angular/router';
import { filter } from 'rxjs/operators';
import { __awaiter } from 'tslib';

class TrackingService {
    constructor(gaConfig, _router, platformId) {
        this._router = _router;
        this.testBrowser = isPlatformBrowser(platformId);
        this._mergedConfig = Object.assign({ trackPageviews: false, trackHotjarGTM: false, enableHotjarEvent: false }, gaConfig);
        if (this._mergedConfig.trackPageviews && this.testBrowser) {
            this._router.events
                .pipe(filter((event) => event instanceof NavigationEnd))
                .subscribe((event) => {
                if (event.urlAfterRedirects.includes('/launch-survey')) {
                    this.gTagEvent('draft_survey', {
                        event_category: 'Survey_Launch',
                        event_label: 'Draft Survey Clicked',
                    });
                }
                if (event.urlAfterRedirects.includes('/dashboard')) {
                    const lastSegment = event.urlAfterRedirects.split('/').pop();
                    const pattern = /^\d+$/;
                    if (lastSegment && pattern.test(lastSegment)) {
                        this.gTagEvent('manage_survey', {
                            event_category: 'Survey_Details',
                            event_label: 'Survey Details Opened',
                        });
                    }
                }
                this._pageView(event);
            });
        }
    }
    gTagEvent(eventName, eventParameters = {}) {
        try {
            if (this._mergedConfig.trackPageviews) {
                gtag('event', eventName, eventParameters);
            }
            if (this._mergedConfig.trackHotjarGTM) {
                const { event_label } = eventParameters;
                this.hotjarEvent(event_label);
            }
        }
        catch (err) {
            console.error('Google Analytics event error', err);
        }
    }
    hotjarEvent(eventName = '') {
        try {
            if (this._mergedConfig.enableHotjarEvent) {
                hj('event', eventName);
            }
        }
        catch (err) {
            console.error('Hotjar event error', err);
        }
    }
    config(params = {}) {
        try {
            if (this._mergedConfig.trackPageviews) {
                gtag('config', this._mergedConfig.trackingId, params);
            }
        }
        catch (err) {
            console.error('Google Analytics config error', err);
        }
    }
    _pageView(event) {
        try {
            const page_title = event.urlAfterRedirects.split('/').pop();
            const params = {
                page_path: event.urlAfterRedirects,
                page_title,
            };
            if (this._mergedConfig.trackPageviews) {
                gtag('set', 'page_title', page_title);
                gtag('config', this._mergedConfig.trackingId, params);
            }
        }
        catch (err) {
            console.error('Google Analytics page view error', err);
        }
    }
}
TrackingService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: TrackingService, deps: [{ token: 'config' }, { token: i1.Router }, { token: PLATFORM_ID }], target: i0.ɵɵFactoryTarget.Injectable });
TrackingService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: TrackingService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: TrackingService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () {
        return [{ type: undefined, decorators: [{
                        type: Inject,
                        args: ['config']
                    }] }, { type: i1.Router }, { type: undefined, decorators: [{
                        type: Inject,
                        args: [PLATFORM_ID]
                    }] }];
    } });

class GtagEventDirective {
    constructor(_track, _renderer, _el) {
        this._track = _track;
        this._renderer = _renderer;
        this._el = _el;
        this.trackOn = '';
        this.action = '';
        this.category = '';
    }
    ngAfterViewInit() {
        try {
            this._renderer.listen(this._el.nativeElement, this.trackOn, () => {
                this._track.gTagEvent(this.action, Object.assign({ event_category: this.category }, this.params));
            });
        }
        catch (err) {
            console.error(err);
        }
    }
}
GtagEventDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: GtagEventDirective, deps: [{ token: TrackingService }, { token: i0.Renderer2 }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive });
GtagEventDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.3.12", type: GtagEventDirective, selector: "[psUiGtagEvent]", inputs: { trackOn: "trackOn", action: "action", category: "category", params: "params" }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: GtagEventDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[psUiGtagEvent]',
                }]
        }], ctorParameters: function () { return [{ type: TrackingService }, { type: i0.Renderer2 }, { type: i0.ElementRef }]; }, propDecorators: { trackOn: [{
                type: Input
            }], action: [{
                type: Input
            }], category: [{
                type: Input
            }], params: [{
                type: Input
            }] } });

class GoogleTagManagerModule {
    static forRoot(config) {
        return {
            ngModule: GoogleTagManagerModule,
            providers: [
                TrackingService,
                { provide: 'config', useValue: config || {} },
            ],
        };
    }
}
GoogleTagManagerModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: GoogleTagManagerModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
GoogleTagManagerModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: GoogleTagManagerModule, declarations: [GtagEventDirective], imports: [CommonModule, RouterModule], exports: [GtagEventDirective] });
GoogleTagManagerModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: GoogleTagManagerModule, imports: [[CommonModule, RouterModule]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: GoogleTagManagerModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [GtagEventDirective],
                    imports: [CommonModule, RouterModule],
                    exports: [GtagEventDirective],
                }]
        }] });

class HotjarEventDirective {
    constructor(_track, _renderer, _el) {
        this._track = _track;
        this._renderer = _renderer;
        this._el = _el;
        this.trackOn = '';
        this.eventName = '';
    }
    ngAfterViewInit() {
        try {
            this._renderer.listen(this._el.nativeElement, this.trackOn, () => {
                this._track.hotjarEvent(this.eventName);
            });
        }
        catch (err) {
            console.error(err);
        }
    }
}
HotjarEventDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: HotjarEventDirective, deps: [{ token: TrackingService }, { token: i0.Renderer2 }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive });
HotjarEventDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.3.12", type: HotjarEventDirective, selector: "[psUiHotjarEvent]", inputs: { trackOn: "trackOn", eventName: "eventName" }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: HotjarEventDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[psUiHotjarEvent]',
                }]
        }], ctorParameters: function () { return [{ type: TrackingService }, { type: i0.Renderer2 }, { type: i0.ElementRef }]; }, propDecorators: { trackOn: [{
                type: Input
            }], eventName: [{
                type: Input
            }] } });

class HotjarModule {
}
HotjarModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: HotjarModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
HotjarModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: HotjarModule, declarations: [HotjarEventDirective], imports: [CommonModule, RouterModule], exports: [HotjarEventDirective] });
HotjarModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: HotjarModule, imports: [[CommonModule, RouterModule]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: HotjarModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [HotjarEventDirective],
                    imports: [CommonModule, RouterModule],
                    exports: [HotjarEventDirective],
                }]
        }] });

const ChurnZeroConsts = {
    events: {
        login: 'Login',
        logout: 'Logout',
        draftSurveyCreated: 'Draft Survey Created',
        draftSurveyUpdated: 'Draft Survey Updated',
        lauchSurveyCreated: 'Launch Survey Created',
        surveyPaused: 'Survey paused',
        surveyClosed: 'Survey closed',
        acceptTos: 'Accepted Terms of Services',
        surveyLaunchedWithNoQuotas: 'Survey Launched With No Quotas',
        lastActive: 'Last Active',
    },
    eventDescriptions: {
        login: 'User Logged',
        logout: 'User Logged out',
        draftSurveyCreated: 'User created a survey (id :id)',
        draftSurveyUpdated: 'User updated a survey (id :id)',
        lauchSurveyCreated: 'Launch Survey Created (id :id)',
        surveyPaused: 'User paused a survey (id :id)',
        surveyClosed: 'User closed a survey (id :id)',
        acceptTos: 'User Accepted Terms of Services',
        surveyLaunchedWithNoQuotas: 'Survey Launched With No Quotas (id :id)',
        lastActive: 'User visited the dashboard',
    },
    fieldName: {
        hasAcceptedOptInCommunication: 'Hasacceptedoptincommunication',
    },
    contact: 'contact',
    AMlink: 'https://analytics.churnzero.net/churnzero.js',
    EUlink: 'https://eu1analytics.churnzero.net/churnzero.js',
    maxNumberConnAttempts: 3,
    connTimeOutMilliseconds: 3000,
};

const CHURNZERO_BASE_URL = new InjectionToken('CHURNZERO_BASE_URL');
class ChurnZeroService {
    constructor(_churnzero_base_url) {
        this._churnzero_base_url = _churnzero_base_url;
        this.isSync = false;
        this._churnZeroKey = '';
        /**
         * @param {string} accountExternalId same as buyer id.
         * @param {string} contactExternalId same as user id.
         */
        this.connect = (user, churnZeroKey) => __awaiter(this, void 0, void 0, function* () {
            this.user = user;
            this._churnZeroKey = churnZeroKey;
            if (this.isSync) {
                this._connectWithChurnZero();
            }
            return true;
        });
        this._connectWithChurnZero = () => __awaiter(this, void 0, void 0, function* () {
            yield this._isConnected();
            this.ChurnZero.push(['setAppKey', this._churnZeroKey]);
            if (this.user) {
                this.ChurnZero.push(['setContact', this.user.cmp, this.user.id]);
            }
            return true;
        });
        /**
         * @returns {boolean} true
         * @param {string} eventName (Required) This is the unique name of the event (i.e Sent Blog Post). If the Event Name is not found it will be created.
         * @param {string} description (Required) A description of the Event (i.e Blog Title)
         * @param {string} quantity (optional) The number related to this event (Commonly used to track things like email sent, etc)
         * @param {string} customfields (optional) Additional custom fields in a name/value pair. Important Note: At this time, custom fields cannot be created on the fly. Please make sure to request any required custom field(s)
         * @example ChurnZero.push(['trackEvent', 'Email Sent', 'Email Subject', 5, {field1: 'field1Value', field2: 'field2Value'}]);
         */
        this.sendEvent = (eventName, description, customFields, quantity) => __awaiter(this, void 0, void 0, function* () {
            yield this._isConnected();
            this.ChurnZero.push([
                'trackEvent',
                eventName,
                description,
                quantity,
                customFields,
            ]);
            return true;
        });
        /**
         * @returns {boolean} true
         * @param {string} entity (Required)- Can be either 'contact' or 'account'
         * @param {string} name (Required)- Name of the field to be updated
         * @param {string} value (Required)- Value to be used for the update. This value must be type compatible.
         * @example ChurnZero.push(['setAttribute', 'contact', 'Profile Enabled', true]);
         */
        this.updateAttribute = (entity, name, value) => __awaiter(this, void 0, void 0, function* () {
            this._isConnected();
            this.ChurnZero.push(['setAttribute', entity, name, value]);
            return true;
        });
        /**
         * @summary Disconect with churnzero
         */
        this.disconnect = () => {
            this._isConnected();
            this.ChurnZero.push([
                'trackEvent',
                ChurnZeroConsts.events.logout,
                ChurnZeroConsts.eventDescriptions.logout,
                1,
                {
                    cf_last_login_date: new Date(this.user.lastLogin).toISOString(),
                },
            ]);
            this.ChurnZero.push(['stop']);
        };
        let cz = document.createElement('script');
        cz.type = 'text/javascript';
        cz.async = true;
        cz.src = this._churnzero_base_url + '/churnzero.js';
        let s = document.getElementsByTagName('script')[0];
        if (s === null || s === void 0 ? void 0 : s.parentNode) {
            s.parentNode.insertBefore(cz, s);
            cz.onload = () => {
                if (!this.isSync) {
                    this._connectWithChurnZero();
                }
                this.isSync = true;
            };
        }
    }
    _isConnected(retryAttempt = 1) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.ChurnZero) {
                if (!window.ChurnZero &&
                    retryAttempt > ChurnZeroConsts.maxNumberConnAttempts) {
                    throw new Error('ChurnZero library not found, check if it was loaded');
                }
                else if (!window.ChurnZero &&
                    retryAttempt <= ChurnZeroConsts.maxNumberConnAttempts) {
                    yield new Promise((resolve) => setTimeout(resolve, ChurnZeroConsts.connTimeOutMilliseconds));
                    const attempt = retryAttempt + 1;
                    this._isConnected(attempt);
                }
                else {
                    this.ChurnZero = window.ChurnZero;
                }
            }
            return true;
        });
    }
}
ChurnZeroService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: ChurnZeroService, deps: [{ token: CHURNZERO_BASE_URL }], target: i0.ɵɵFactoryTarget.Injectable });
ChurnZeroService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: ChurnZeroService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: ChurnZeroService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () {
        return [{ type: undefined, decorators: [{
                        type: Inject,
                        args: [CHURNZERO_BASE_URL]
                    }] }];
    } });

class ChurnZeroModule {
    static forRoot(churnzeroBaseUrl) {
        return {
            ngModule: ChurnZeroModule,
            providers: [
                {
                    provide: CHURNZERO_BASE_URL,
                    useValue: churnzeroBaseUrl,
                },
                ChurnZeroService,
            ],
        };
    }
}
ChurnZeroModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: ChurnZeroModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
ChurnZeroModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: ChurnZeroModule });
ChurnZeroModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: ChurnZeroModule, providers: [ChurnZeroService] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: ChurnZeroModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [],
                    providers: [ChurnZeroService],
                }]
        }] });

const NEWRELIC = new InjectionToken('NEWRELIC');

/**
 * Generated bundle index. Do not edit.
 */

export { CHURNZERO_BASE_URL, ChurnZeroConsts, ChurnZeroModule, ChurnZeroService, GoogleTagManagerModule, GtagEventDirective, HotjarEventDirective, HotjarModule, NEWRELIC };
